// client/src/pages/admin/ZoomBroadcast.tsx
// Super-user tool to compose a message and push it to users' Zoom bot (Keith
// Botfield) via the existing logic-zoombot-sendMessage pipeline.
import React, { useEffect, useMemo, useState } from 'react';
import { Send, Users, RefreshCw, Search, MessageSquare, Link2, AlertTriangle } from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuth } from '../../auth/AuthContext';
import { getRoster, sendBroadcast, type RosterEntry } from '../../services/zoomBroadcast';

const ACCENT = '#0e639c';

// ─── Minimal Zoom-markdown preview (bold / italic / strike / code) ─────────────
function renderMarkdown(text: string): string {
  const esc = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return esc
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[^_])_(?!_)(.+?)_(?!_)/g, '$1<em>$2</em>')
    .replace(/~(.+?)~/g, '<del>$1</del>')
    .replace(/`(.+?)`/g, '<code style="font-family:monospace;background:rgba(255,255,255,.08);padding:1px 4px;border-radius:3px">$1</code>')
    .replace(/\n/g, '<br/>');
}

export default function ZoomBroadcast() {
  const { ready, isAdmin } = useAuth();

  const [roster, setRoster]       = useState<RosterEntry[]>([]);
  const [cachedAt, setCachedAt]   = useState<string>('');
  const [loading, setLoading]     = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [title, setTitle]         = useState('');
  const [message, setMessage]     = useState('');
  const [linkLabel, setLinkLabel] = useState('');
  const [linkUrl, setLinkUrl]     = useState('');

  const [broadcastAll, setBroadcastAll] = useState(false);
  const [selected, setSelected]   = useState<Set<string>>(new Set());
  const [search, setSearch]       = useState('');
  const [sending, setSending]     = useState(false);

  useEffect(() => {
    if (!ready || !isAdmin) return;
    loadRoster(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, isAdmin]);

  async function loadRoster(refresh: boolean) {
    refresh ? setRefreshing(true) : setLoading(true);
    try {
      const data = await getRoster(refresh);
      setRoster(data.roster);
      setCachedAt(data.cachedAt);
    } catch (e) {
      toast.error('Failed to load roster: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return roster;
    return roster.filter(r => r.email.includes(q) || r.name.toLowerCase().includes(q));
  }, [roster, search]);

  function toggle(email: string) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(email) ? next.delete(email) : next.add(email);
      return next;
    });
  }

  const recipientCount = broadcastAll ? roster.length : selected.size;

  async function handleSend() {
    if (!message.trim()) { toast.error('Message body is required.'); return; }
    if (recipientCount === 0) { toast.error('Pick at least one recipient (or choose All users).'); return; }
    if (linkUrl.trim() && !/^https?:\/\//i.test(linkUrl.trim())) {
      toast.error('Link URL must start with http:// or https://'); return;
    }

    const who = broadcastAll
      ? `ALL ${roster.length} enrolled users`
      : `${selected.size} user${selected.size === 1 ? '' : 's'}`;
    if (!window.confirm(`Send this Zoom message to ${who}? This cannot be unsent.`)) return;

    setSending(true);
    try {
      const res = await sendBroadcast({
        broadcastAll,
        recipients: broadcastAll ? undefined : Array.from(selected),
        title: title.trim() || undefined,
        message: message.trim(),
        linkLabel: linkLabel.trim() || undefined,
        linkUrl: linkUrl.trim() || undefined,
      });
      toast.success(`Queued for ${res.sentTo} recipient${res.sentTo === 1 ? '' : 's'}. Delivery runs in the background.`);
    } catch (e) {
      toast.error('Send failed: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setSending(false);
    }
  }

  if (!ready) return null;
  if (!isAdmin) {
    return (
      <section className="card">
        <div className="bd"><div className="muted">You do not have access to this page.</div></div>
      </section>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Header */}
      <div>
        <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-.01em' }}>Zoom Broadcast</div>
        <div className="muted" style={{ fontSize: 13, marginTop: 3 }}>
          Push a message (with an optional link back into an app) to users through the Zoom bot, Keith Botfield.
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 16, alignItems: 'start' }}>
        {/* ── Left: compose + preview ─────────────────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card" style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Field label="Headline (optional)">
              <input className="input" type="text" value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="e.g. New Sprint Board is live"
                style={{ width: '100%', fontSize: 14 }} />
            </Field>

            <Field label="Message" hint="Supports Zoom markdown: **bold**, _italic_, ~strike~, `code`">
              <textarea className="input" value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder="Type your message…"
                rows={5}
                style={{ width: '100%', fontSize: 13, resize: 'vertical' }} />
            </Field>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Link text (optional)">
                <input className="input" type="text" value={linkLabel}
                  onChange={e => setLinkLabel(e.target.value)}
                  placeholder="Open in Admin HQ"
                  style={{ width: '100%', fontSize: 13 }} />
              </Field>
              <Field label="Link URL (optional)">
                <input className="input" type="text" value={linkUrl}
                  onChange={e => setLinkUrl(e.target.value)}
                  placeholder="https://adminhq.botfield.ai/…"
                  style={{ width: '100%', fontSize: 13, fontFamily: 'monospace' }} />
              </Field>
            </div>
          </div>

          {/* Preview */}
          <div className="card" style={{ padding: '18px 20px' }}>
            <div className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 10 }}>
              Preview
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ width: 4, borderRadius: 3, background: ACCENT, flexShrink: 0 }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                {title.trim() && <div style={{ fontSize: 14, fontWeight: 700 }}>{title}</div>}
                <div style={{ fontSize: 13, lineHeight: 1.5 }}
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(message || '_Your message preview appears here._') }} />
                {linkUrl.trim() && (
                  <a href={linkUrl} target="_blank" rel="noreferrer"
                    style={{ fontSize: 13, color: '#7aa2ff', display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 2 }}>
                    <Link2 size={13} /> {linkLabel.trim() || linkUrl}
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Send */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="btn primary" type="button" disabled={sending}
              onClick={handleSend} style={{ gap: 7, fontSize: 14, padding: '9px 16px' }}>
              <Send size={15} />
              {sending ? 'Sending…' : `Send to ${recipientCount} user${recipientCount === 1 ? '' : 's'}`}
            </button>
            {broadcastAll && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#ffc107' }}>
                <AlertTriangle size={13} /> Broadcasting to everyone
              </span>
            )}
          </div>
        </div>

        {/* ── Right: recipients ───────────────────────────────────────────── */}
        <div className="card" style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Users size={15} style={{ color: ACCENT }} />
            <span style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>Recipients</span>
            <button className="icon-btn" type="button" title="Refresh roster from Zoom"
              disabled={refreshing} onClick={() => loadRoster(true)} style={{ padding: 3 }}>
              <RefreshCw size={14} className={refreshing ? 'spin' : ''} />
            </button>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer',
            padding: '8px 10px', borderRadius: 8, background: broadcastAll ? `${ACCENT}18` : 'rgba(255,255,255,.03)',
            border: `1px solid ${broadcastAll ? ACCENT : 'var(--border)'}` }}>
            <input type="checkbox" checked={broadcastAll} onChange={e => setBroadcastAll(e.target.checked)} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>All users</div>
              <div className="muted" style={{ fontSize: 11 }}>{roster.length} enrolled with the bot</div>
            </div>
          </label>

          {!broadcastAll && (
            <>
              <div style={{ position: 'relative' }}>
                <Search size={13} style={{ position: 'absolute', left: 9, top: 9, color: 'var(--muted)' }} />
                <input className="input" type="text" value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search name or email"
                  style={{ width: '100%', fontSize: 12, paddingLeft: 28 }} />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11 }}>
                <span className="muted" style={{ flex: 1 }}>
                  {selected.size} selected{cachedAt ? ` · roster ${new Date(cachedAt).toLocaleDateString()}` : ''}
                </span>
                <button className="btn" type="button" style={{ fontSize: 10, padding: '2px 7px' }}
                  onClick={() => setSelected(new Set(filtered.map(r => r.email)))}>Select shown</button>
                <button className="btn" type="button" style={{ fontSize: 10, padding: '2px 7px' }}
                  onClick={() => setSelected(new Set())}>Clear</button>
              </div>

              <div style={{ maxHeight: 380, overflowY: 'auto', display: 'flex', flexDirection: 'column',
                gap: 2, border: '1px solid var(--border)', borderRadius: 8, padding: 6 }}>
                {loading ? (
                  <div className="muted" style={{ fontSize: 12, padding: 8 }}>Loading roster…</div>
                ) : filtered.length === 0 ? (
                  <div className="muted" style={{ fontSize: 12, padding: 8 }}>No matches.</div>
                ) : filtered.map(r => (
                  <label key={r.email} style={{ display: 'flex', alignItems: 'center', gap: 8,
                    padding: '5px 7px', borderRadius: 5, cursor: 'pointer',
                    background: selected.has(r.email) ? `${ACCENT}12` : 'transparent' }}>
                    <input type="checkbox" checked={selected.has(r.email)} onChange={() => toggle(r.email)} />
                    <span style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.email}
                    </span>
                  </label>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="muted" style={{ fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <MessageSquare size={12} /> Messages deliver through Keith Botfield. Only users who have installed/authorized the bot can receive them.
      </div>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', display: 'block', marginBottom: 5 }}>
        {label}
      </label>
      {children}
      {hint && <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>{hint}</div>}
    </div>
  );
}
